import {Injectable, inject} from '@angular/core';
import {Router} from '@angular/router';
import {OAuthErrorParams, OAuthErrorType} from './auth-event';

@Injectable({
  providedIn: 'root'
})
export class OAuthErrorService {
  private router = inject(Router);

  /**
   * Extracts OAuth error parameters from the current URL
   * @returns OAuthErrorParams if error parameters are present, null otherwise
   */
  extractErrorFromUrl(): OAuthErrorParams | null {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');

    if (!error) {
      return null;
    }

    return {
      error,
      error_description: urlParams.get('error_description') || undefined,
      error_uri: urlParams.get('error_uri') || undefined,
      iss: urlParams.get('iss') || undefined,
      state: urlParams.get('state') || undefined
    };
  }

  /**
   * Determines the OAuth error type from error string
   * @param errorString The error string from OAuth provider
   * @returns OAuthErrorType enum value
   */
  getErrorType(errorString: string): OAuthErrorType {
    switch (errorString) {
      case 'temporarily_unavailable':
        return OAuthErrorType.TEMPORARILY_UNAVAILABLE;
      case 'authentication_expired':
        return OAuthErrorType.AUTHENTICATION_EXPIRED;
      case 'access_denied':
        return OAuthErrorType.ACCESS_DENIED;
      case 'invalid_request':
        return OAuthErrorType.INVALID_REQUEST;
      case 'invalid_scope':
        return OAuthErrorType.INVALID_SCOPE;
      case 'server_error':
        return OAuthErrorType.SERVER_ERROR;
      default:
        return OAuthErrorType.UNKNOWN;
    }
  }

  /**
   * Gets a user-friendly error message based on error type
   * @param errorType The OAuth error type
   * @param errorDescription Optional detailed error description from provider
   * @returns User-friendly error message
   */
  getUserFriendlyMessage(errorType: OAuthErrorType, errorDescription?: string): string {
    switch (errorType) {
      case OAuthErrorType.TEMPORARILY_UNAVAILABLE:
        return 'The authentication service is temporarily unavailable. Please try again in a moment.';
      case OAuthErrorType.AUTHENTICATION_EXPIRED:
        return 'Your authentication session has expired. Please log in again.';
      case OAuthErrorType.ACCESS_DENIED:
        return 'Access was denied. If you did not cancel the login, please contact support.';
      case OAuthErrorType.INVALID_REQUEST:
        return 'There was a problem with the authentication request. Please try logging in again.';
      case OAuthErrorType.INVALID_SCOPE:
        return 'The requested permissions are not valid. Please contact support.';
      case OAuthErrorType.SERVER_ERROR:
        return 'The authentication server encountered an error. Please try again later.';
      default:
        return errorDescription || 'An authentication error occurred. Please try logging in again.';
    }
  }

  /**
   * Checks if an error type is retriable
   * @param errorType The OAuth error type
   * @returns true if the error is retriable
   */
  isRetriableError(errorType: OAuthErrorType): boolean {
    return errorType === OAuthErrorType.TEMPORARILY_UNAVAILABLE ||
           errorType === OAuthErrorType.SERVER_ERROR;
  }

  /**
   * Cleans OAuth error parameters from the current URL
   * Removes error, error_description, error_uri, iss, and state parameters
   */
  cleanUrlParameters(): void {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    // Remove OAuth error parameters
    params.delete('error');
    params.delete('error_description');
    params.delete('error_uri');
    params.delete('iss');
    params.delete('state');

    // Update URL without reloading the page
    const newUrl = params.toString() ? `${url.pathname}?${params.toString()}` : url.pathname;
    window.history.replaceState({}, '', newUrl);
  }

  /**
   * Logs OAuth error details for debugging
   * @param errorParams The OAuth error parameters
   */
  logError(errorParams: OAuthErrorParams): void {
    console.error('OAuth Error:', {
      error: errorParams.error,
      description: errorParams.error_description,
      issuer: errorParams.iss,
      error_uri: errorParams.error_uri,
      state: errorParams.state,
      timestamp: new Date().toISOString(),
      url: window.location.href
    });
  }
}
